import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectAcceptedEngagementCookies,
  selectAuth,
  selectAuthStage,
  selectIdentitySwitching,
  selectIsExternalUser,
  selectIsImpersonated,
  selectRefiner,
  selectRestoringSession,
} from "@/data/selectors/auth";
import { identifyRefinerUser, resetRefinerClient } from "@/lib/refiner";

function compact(value) {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) => item !== null && item !== undefined && item !== "",
    ),
  );
}

function normalizeLocale(locale) {
  if (!locale) {
    return "";
  }

  return String(locale).split(/[-_]/)[0].toLowerCase();
}

function toIsoDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

function buildFullName(firstName, lastName) {
  return [firstName, lastName].filter(Boolean).join(" ").trim();
}

function buildRefinerUser(auth) {
  const firstName = auth?.firstName || "";
  const lastName = auth?.lastName || "";
  const fullName = buildFullName(firstName, lastName);

  return compact({
    id: auth?.userId ? String(auth.userId) : null,
    email: auth?.email || null,
    name: fullName || auth?.email || null,
    first_name: firstName || null,
    last_name: lastName || null,
    signed_up_at: toIsoDate(auth?.userCreationDate),
  });
}

export default function useRefinerClient() {
  const { i18n } = useTranslation();

  const auth = useSelector(selectAuth);
  const authStage = useSelector(selectAuthStage);
  const refiner = useSelector(selectRefiner);
  const restoringSession = useSelector(selectRestoringSession);
  const identitySwitching = useSelector(selectIdentitySwitching);
  const isExternalUser = useSelector(selectIsExternalUser);
  const isImpersonated = useSelector(selectIsImpersonated);
  const acceptedEngagementCookies = useSelector(
    selectAcceptedEngagementCookies,
  );

  const didTouchRefinerRef = useRef(false);
  const syncIdRef = useRef(0);

  const locale = normalizeLocale(i18n.resolvedLanguage || i18n.language);

  const refinerContext = useMemo(() => {
    if (
      restoringSession ||
      identitySwitching ||
      authStage !== "authenticated" ||
      acceptedEngagementCookies !== true ||
      !isExternalUser ||
      isImpersonated ||
      !refiner?.projectId
    ) {
      return null;
    }

    const user = buildRefinerUser(auth);

    if (!user.id && !user.email) {
      return null;
    }

    return {
      projectId: refiner.projectId,
      locale,
      user,
    };
  }, [
    acceptedEngagementCookies,
    auth?.email,
    auth?.firstName,
    auth?.lastName,
    auth?.userCreationDate,
    auth?.userId,
    authStage,
    identitySwitching,
    isExternalUser,
    isImpersonated,
    locale,
    refiner?.projectId,
    restoringSession,
  ]);

  useEffect(() => {
    const syncId = syncIdRef.current + 1;
    syncIdRef.current = syncId;

    const isCurrentSync = () => syncIdRef.current === syncId;

    async function syncRefiner() {
      if (!refinerContext) {
        if (didTouchRefinerRef.current) {
          await resetRefinerClient();

          if (isCurrentSync()) {
            didTouchRefinerRef.current = false;
          }
        }

        return;
      }

      try {
        const identified = await identifyRefinerUser(refinerContext);

        if (!isCurrentSync() || !identified) {
          return;
        }

        didTouchRefinerRef.current = true;
      } catch (error) {
        console.warn("Failed to synchronize Refiner client", error);
      }
    }

    void syncRefiner();

    return undefined;
  }, [refinerContext]);

  return null;
}
